import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

/**
 * A real map for the activity heat map: one circle per city, sized by activity, on a
 * monochrome light style; hover any circle for the city and its count. Falls back to a
 * plain list when no Mapbox token is configured — or when the device has no WebGL (an
 * old phone, a WebView with it off): Mapbox throws from its constructor there, and that
 * used to take the whole ANALYTICS page down with it.
 */
export default function ActivityMap({ cities }) {
  const el = useRef(null);
  const mapRef = useRef(null);
  const [noGl, setNoGl] = useState(false);
  const pts = (cities || []).filter((c) => c.lat != null && c.lng != null);

  useEffect(() => {
    if (!TOKEN || noGl || !el.current || pts.length === 0) return undefined;
    mapboxgl.accessToken = TOKEN;
    let map;
    try {
      map = new mapboxgl.Map({ container: el.current, style: 'mapbox://styles/mapbox/light-v11', attributionControl: false, cooperativeGestures: true });
    } catch {
      setNoGl(true);
      return undefined;
    }
    mapRef.current = map;
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    const max = Math.max(1, ...pts.map((p) => p.count));
    const bounds = new mapboxgl.LngLatBounds();
    pts.forEach((p) => bounds.extend([p.lng, p.lat]));
    map.on('load', () => {
      map.addSource('activity', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: pts.map((p) => ({ type: 'Feature', properties: { city: p.city, count: p.count }, geometry: { type: 'Point', coordinates: [p.lng, p.lat] } })) },
      });
      map.addLayer({
        id: 'activity-circles', type: 'circle', source: 'activity',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['get', 'count'], 1, 6, max, 28],
          'circle-color': '#000000', 'circle-opacity': 0.28, 'circle-stroke-color': '#000000', 'circle-stroke-width': 2,
        },
      });
      map.addLayer({
        id: 'activity-labels', type: 'symbol', source: 'activity',
        filter: ['>=', ['get', 'count'], max * 0.35],
        layout: { 'text-field': ['get', 'city'], 'text-size': 11, 'text-offset': [0, 1.6], 'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'] },
        paint: { 'text-color': '#000000', 'text-halo-color': '#FFFFFF', 'text-halo-width': 1.5 },
      });
      const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 12, className: 'pr-analytics-popup' });
      map.on('mousemove', 'activity-circles', (e) => {
        map.getCanvas().style.cursor = 'pointer';
        const f = e.features[0];
        popup.setLngLat(f.geometry.coordinates).setText(`${f.properties.city} — ${Number(f.properties.count).toLocaleString()} events`).addTo(map);
      });
      map.on('mouseleave', 'activity-circles', () => { map.getCanvas().style.cursor = ''; popup.remove(); });
      map.fitBounds(bounds, { padding: 48, maxZoom: 9, duration: 0 });
    });
    return () => { map.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(pts), noGl]);

  if (pts.length === 0) return <div className="py-8 text-center font-mono text-[10px] uppercase tracking-[0.1em] text-[#9CA3AF]">NO GEOLOCATED ACTIVITY YET.</div>;
  if (!TOKEN || noGl) {
    return (
      <div className="font-mono text-[11px] text-[#6B7280]" data-testid="activity-map-list">
        <div className="mb-2 font-bold uppercase tracking-[0.1em]">{noGl ? 'MAP UNAVAILABLE — THIS DEVICE CANNOT DRAW IT. BY CITY:' : 'MAP UNAVAILABLE — REACT_APP_MAPBOX_TOKEN IS NOT SET.'}</div>
        {pts.slice(0, 12).map((p) => <div key={p.city}>{p.city} · {p.count.toLocaleString()}</div>)}
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-[18px] bg-[#F7F7F7]">
      <style>{`.pr-analytics-popup .mapboxgl-popup-content{border:2px solid #000;border-radius:0;box-shadow:4px 4px 0 0 #000;font-family:JetBrains Mono,monospace;font-size:11px;padding:6px 10px}.pr-analytics-popup .mapboxgl-popup-tip{display:none}`}</style>
      <div ref={el} style={{ height: 380 }} />
    </div>
  );
}
